/**
 * 상점 광고 다이아 상품 데이터 (shopAd.tsv → generated/shopAd.json 재구성).
 * 일반 상점 고정 행 — 광고 시청 보상 상품. 보상은 기존 통합 RewardSpec 재사용.
 * 수치(보상량/쿨타임 초)는 `data/shopAd.tsv`에서 수정 후 `npm run export:data`로 재생성한다.
 *
 * gateKind = 재구매 게이트:
 * - "cooldown": cooldownSeconds 만큼 재사용 불가 (@yjsgames/cooldown). 예: 2시간마다 광고 다이아.
 * - "daily":   하루 1회 (@yjsgames/daily-reset, 자정 리셋). cooldownSeconds 무시(0).
 */
import { BigNumber } from "@yjsgames/bignumber";
import shopAdJson from "./generated/shopAd.json";
import type { RewardKind, RewardSpec } from "../models/RewardSpec.js";

/** 재구매 게이트 종류. */
export type ShopAdGateKind = "cooldown" | "daily";

/** 광고 다이아 상품 1행 — 보상(kind/amount) + 게이트(cooldown/daily) + i18n/아이콘 키. */
export interface ShopAdProduct {
  readonly id: string;
  readonly rewardKind: RewardKind;
  readonly rewardAmount: number;
  readonly gateKind: ShopAdGateKind;
  /** cooldown 게이트의 쿨타임(초). daily면 0(무시). */
  readonly cooldownSeconds: number;
  readonly nameKey: string;
  readonly descKey: string;
  /** 카드 좌측 상품 아이콘 키(얻는 것=보상). 데이터로 제어 — 전용 이미지 준비 시 tsv만 교체. */
  readonly iconKey: string;
  /** 구매 버튼 아이콘 키(행위=광고 시청). 카드 아이콘(보상)과 역할 분리. */
  readonly buttonIconKey: string;
}

export const SHOP_AD_PRODUCTS: readonly ShopAdProduct[] = shopAdJson.map((row) => ({
  id: row.id,
  rewardKind: row.rewardKind as RewardKind,
  rewardAmount: row.rewardAmount,
  gateKind: row.gateKind as ShopAdGateKind,
  cooldownSeconds: row.cooldownSeconds,
  nameKey: row.nameKey,
  descKey: row.descKey,
  iconKey: row.iconKey,
  buttonIconKey: row.buttonIconKey,
}));

/** id로 상품 조회. 미존재 시 undefined. */
export function shopAdProduct(id: string): ShopAdProduct | undefined {
  return SHOP_AD_PRODUCTS.find((p) => p.id === id);
}

/** 상품 → RewardSpec (resolveRewards 입력용). MVP는 diamond만 — 확장 시 dailyShop과 동형 분기. */
export function shopAdRewardSpec(product: ShopAdProduct): RewardSpec {
  switch (product.rewardKind) {
    case "diamond":
      return { kind: "diamond", amount: BigNumber.fromNumber(product.rewardAmount).toString() };
    case "gold":
      return { kind: "gold", amount: BigNumber.fromNumber(product.rewardAmount).toString() };
    case "upgradeStone":
      return { kind: "upgradeStone", amount: product.rewardAmount };
    case "rebirthPoint":
      return { kind: "rebirthPoint", amount: product.rewardAmount };
    default:
      throw new Error(`[shopAdData] unsupported reward kind: ${String(product.rewardKind)}`);
  }
}
